import {
  ApartmentOutlined,
  CloseOutlined,
  LoadingOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { request } from '@umijs/max';
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Space,
  Spin,
  Tree,
  Typography,
} from 'antd';
import type { DataNode, EventDataNode } from 'antd/es/tree';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const defaultApi = '/api/crm/admin/department';

export type DepartmentMemberSelectionType = 'department' | 'employee';
export type DepartmentMemberSelectionMode = 'none' | 'single' | 'multiple';

export type DepartmentMemberSelectionItem = {
  key: string;
  id: string;
  type: DepartmentMemberSelectionType;
  name: string;
  path: string;
  disabled?: boolean;
  readOnly?: boolean;
  readOnlyReason?: string;
};

export type DepartmentMemberDepartment = {
  id: string;
  parentId: string;
  name: string;
  hasChildren?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  readOnlyReason?: string;
};

export type DepartmentMemberEmployee = {
  id: string;
  name: string;
  disabled?: boolean;
};

export type DepartmentMemberSelectorProps = {
  open: boolean;
  value?: DepartmentMemberSelectionItem[];
  title?: React.ReactNode;
  width?: number;
  departmentSelectionMode?: DepartmentMemberSelectionMode;
  employeeSelectionMode?: DepartmentMemberSelectionMode;
  confirmLoading?: boolean;
  departmentApi?: string;
  /** 只用于手工部门配置；普通筛选、权限及成员选择仍可选择同步部门。 */
  disableReadOnlyDepartments?: boolean;
  loadDepartments?: (parentId: string) => Promise<DepartmentMemberDepartment[]>;
  loadMembers?: (departmentId: string) => Promise<DepartmentMemberEmployee[]>;
  /** 使用业务接口已授权的员工选项；仅选成员时平铺显示，并在本地搜索。 */
  employeeOptions?: DepartmentMemberEmployee[];
  onChange?: (value: DepartmentMemberSelectionItem[]) => void;
  onConfirm?: (value: DepartmentMemberSelectionItem[]) => void | Promise<void>;
  onCancel: () => void;
};

type SelectorTreeNode = DataNode & {
  nodeType: DepartmentMemberSelectionType;
  nodeId: string;
  path: string;
  selectionItem: DepartmentMemberSelectionItem;
  contentLoaded?: boolean;
  children?: SelectorTreeNode[];
};

const responseItems = (response: any): any[] => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const updateTreeNode = (
  nodes: SelectorTreeNode[],
  key: React.Key,
  children: SelectorTreeNode[],
): SelectorTreeNode[] =>
  nodes.map((node) =>
    node.key === key
      ? {
          ...node,
          children,
          contentLoaded: true,
          isLeaf: children.length === 0,
        }
      : {
          ...node,
          children: node.children
            ? updateTreeNode(node.children, key, children)
            : undefined,
        },
  );

const flattenTree = (nodes: SelectorTreeNode[]): SelectorTreeNode[] => {
  const result: SelectorTreeNode[] = [];
  const walk = (items: SelectorTreeNode[]) => {
    items.forEach((item) => {
      result.push(item);
      walk(item.children ?? []);
    });
  };
  walk(nodes);
  return result;
};

const DepartmentMemberSelector: React.FC<DepartmentMemberSelectorProps> = ({
  open,
  value = [],
  title = '选择部门或成员',
  width = 840,
  departmentSelectionMode = 'multiple',
  employeeSelectionMode = 'multiple',
  confirmLoading = false,
  departmentApi = defaultApi,
  disableReadOnlyDepartments = false,
  loadDepartments,
  loadMembers,
  employeeOptions,
  onChange,
  onConfirm,
  onCancel,
}) => {
  const requestVersionRef = useRef(0);
  const searchRequestVersionRef = useRef(0);
  const [tree, setTree] = useState<SelectorTreeNode[]>([]);
  const [draftValue, setDraftValue] = useState<DepartmentMemberSelectionItem[]>(
    [],
  );
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<
    DepartmentMemberSelectionItem[]
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const effectiveEmployeeSelectionMode =
    departmentSelectionMode === 'single' ? 'none' : employeeSelectionMode;
  const flatEmployees =
    departmentSelectionMode === 'none' ? employeeOptions : undefined;
  const selectionMode = (type: DepartmentMemberSelectionType) =>
    type === 'department'
      ? departmentSelectionMode
      : effectiveEmployeeSelectionMode;
  const canSelect = (type: DepartmentMemberSelectionType) =>
    selectionMode(type) !== 'none';
  const selectionTargetLabel =
    departmentSelectionMode !== 'none' &&
    effectiveEmployeeSelectionMode !== 'none'
      ? '部门或成员'
      : departmentSelectionMode !== 'none'
        ? '部门'
        : '成员';

  const requestDepartments = async (parentId: string) => {
    if (loadDepartments) return loadDepartments(parentId);
    const response = await request(`${departmentApi}/tree`, {
      params: { parentId },
    });
    return responseItems(response).map((department) => ({
      id: String(department.id),
      parentId: String(department.parentId ?? department.parent_id ?? '0'),
      name: String(department.name),
      hasChildren: department.hasChildren ?? department.has_children,
      disabled: Number(department.status ?? 1) !== 1,
      readOnly: Boolean(department.readOnly ?? department.read_only),
      readOnlyReason: department.readOnlyReason ?? department.read_only_reason,
    }));
  };

  const requestMembers = async (departmentId: string) => {
    if (loadMembers) return loadMembers(departmentId);
    const response = await request(`${departmentApi}/members`, {
      data: { departmentId, current: 1, pageSize: 1000 },
    });
    return responseItems(response).map((employee) => ({
      id: String(employee.id),
      name: String(employee.name),
      disabled: employee.status !== 'normal',
    }));
  };

  const departmentNode = (
    department: DepartmentMemberDepartment,
    parentPath = '',
  ): SelectorTreeNode => {
    const path = parentPath
      ? `${parentPath}/${department.name}`
      : department.name;
    const item: DepartmentMemberSelectionItem = {
      key: `department:${department.id}`,
      id: department.id,
      type: 'department',
      name: department.name,
      path,
      disabled:
        department.disabled ||
        (disableReadOnlyDepartments && department.readOnly),
      readOnly: department.readOnly,
      readOnlyReason: department.readOnlyReason,
    };
    return {
      key: item.key,
      nodeType: 'department',
      nodeId: department.id,
      path,
      title: (
        <span
          title={
            disableReadOnlyDepartments && department.readOnly
              ? department.readOnlyReason
              : undefined
          }
        >
          {department.name}
        </span>
      ),
      icon: <ApartmentOutlined />,
      disabled: item.disabled,
      selectable: false,
      checkable: canSelect('department'),
      // 仅选部门时使用接口的下级标记；选成员时还需展开加载成员。
      isLeaf:
        effectiveEmployeeSelectionMode === 'none' &&
        department.hasChildren === false,
      selectionItem: item,
    };
  };

  const employeeNode = (
    employee: DepartmentMemberEmployee,
    departmentId: string,
    parentPath: string,
  ): SelectorTreeNode => {
    const path = parentPath ? `${parentPath}/${employee.name}` : employee.name;
    const item: DepartmentMemberSelectionItem = {
      key: `employee:${employee.id}`,
      id: employee.id,
      type: 'employee',
      name: employee.name,
      path,
      disabled: employee.disabled,
    };
    return {
      key: `employee:${departmentId}:${employee.id}`,
      nodeType: 'employee',
      nodeId: employee.id,
      path,
      title: employee.name,
      icon: <UserOutlined />,
      disabled: employee.disabled,
      selectable: false,
      checkable: canSelect('employee'),
      isLeaf: true,
      selectionItem: item,
    };
  };

  useEffect(() => {
    if (!open) return;
    const requestVersion = ++requestVersionRef.current;
    const initialValue: DepartmentMemberSelectionItem[] = [];
    value.forEach((original) => {
      const item = {
        ...original,
        disabled:
          original.disabled ||
          (disableReadOnlyDepartments && original.readOnly),
      };
      const mode = selectionMode(item.type);
      if (mode === 'none') return;
      if (mode === 'single') {
        if (!initialValue.some((selected) => selected.type === item.type)) {
          initialValue.push(item);
        }
        return;
      }
      if (!initialValue.some((selected) => selected.key === item.key)) {
        initialValue.push(item);
      }
    });
    setDraftValue(initialValue);
    setKeyword('');
    setSearchKeyword('');
    if (flatEmployees !== undefined) {
      setTree(flatEmployees.map((employee) => employeeNode(employee, '', '')));
      setInitialLoading(false);
      return;
    }
    setInitialLoading(true);
    void requestDepartments('0')
      .then((departments) => {
        if (requestVersion !== requestVersionRef.current) return;
        setTree(departments.map((department) => departmentNode(department)));
      })
      .finally(() => {
        if (requestVersion === requestVersionRef.current) {
          setInitialLoading(false);
        }
      });
  }, [open, flatEmployees, disableReadOnlyDepartments]);

  useEffect(() => {
    if (!open || searchKeyword === '') {
      searchRequestVersionRef.current += 1;
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    const requestVersion = ++searchRequestVersionRef.current;
    if (flatEmployees !== undefined) {
      setSearchResults(
        flatEmployees
          .filter((employee) => employee.name.includes(searchKeyword))
          .map((employee) => employeeNode(employee, '', '').selectionItem),
      );
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    void request(`${departmentApi}/search`, {
      data: {
        keyword: searchKeyword,
        includeDepartments: departmentSelectionMode !== 'none',
        includeEmployees: effectiveEmployeeSelectionMode !== 'none',
      },
    })
      .then((response) => {
        if (requestVersion !== searchRequestVersionRef.current) return;
        setSearchResults(
          responseItems(response).map((item) => ({
            key: String(item.key ?? `${item.type}:${item.id}`),
            id: String(item.id),
            type: item.type as DepartmentMemberSelectionType,
            name: String(item.name),
            path: String(item.path ?? item.name),
            disabled:
              Boolean(item.disabled) ||
              (disableReadOnlyDepartments &&
                Boolean(item.readOnly ?? item.read_only)),
            readOnly: Boolean(item.readOnly ?? item.read_only),
            readOnlyReason: item.readOnlyReason ?? item.read_only_reason,
          })),
        );
      })
      .finally(() => {
        if (requestVersion === searchRequestVersionRef.current) {
          setSearchLoading(false);
        }
      });
  }, [
    open,
    searchKeyword,
    departmentApi,
    departmentSelectionMode,
    effectiveEmployeeSelectionMode,
    flatEmployees,
    disableReadOnlyDepartments,
  ]);

  const loadNodeContent = async (node: EventDataNode<DataNode>) => {
    const department = node as unknown as SelectorTreeNode;
    if (department.nodeType !== 'department' || department.contentLoaded) {
      return;
    }
    const [departments, employees] = await Promise.all([
      requestDepartments(department.nodeId),
      effectiveEmployeeSelectionMode === 'none'
        ? Promise.resolve([])
        : requestMembers(department.nodeId),
    ]);
    const children = [
      ...departments.map((item) => departmentNode(item, department.path)),
      ...employees.map((item) =>
        employeeNode(item, department.nodeId, department.path),
      ),
    ];
    setTree((current) => updateTreeNode(current, department.key, children));
  };

  const loadedNodes = useMemo(() => flattenTree(tree), [tree]);
  const selectedKeyMap = useMemo(
    () => new Set(draftValue.map((item) => item.key)),
    [draftValue],
  );
  const checkedTreeKeys = useMemo(
    () =>
      loadedNodes
        .filter((node) => selectedKeyMap.has(node.selectionItem.key))
        .map((node) => node.key),
    [loadedNodes, selectedKeyMap],
  );

  const toggleItem = (item: DepartmentMemberSelectionItem) => {
    if (item.disabled || !canSelect(item.type)) return;
    setDraftValue((current) => {
      if (
        current.some(
          (selected) => selected.key === item.key && selected.disabled,
        )
      )
        return current;
      if (
        selectionMode(item.type) === 'single' &&
        current.some(
          (selected) => selected.type === item.type && selected.disabled,
        )
      )
        return current;
      const exists = current.some((selected) => selected.key === item.key);
      if (exists)
        return current.filter((selected) => selected.key !== item.key);
      const mode = selectionMode(item.type);
      if (mode === 'none') return current;
      if (mode === 'single') {
        return [
          ...current.filter((selected) => selected.type !== item.type),
          item,
        ];
      }
      return [...current, item];
    });
  };

  const confirm = async () => {
    if (submitting || confirmLoading) return;
    setSubmitting(true);
    try {
      await onConfirm?.(draftValue);
      onChange?.(draftValue);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      width={width}
      onCancel={onCancel}
      onOk={confirm}
      okText="确认"
      cancelText="取消"
      confirmLoading={submitting || confirmLoading}
      cancelButtonProps={{ disabled: submitting || confirmLoading }}
      closable={!submitting && !confirmLoading}
      maskClosable={!submitting && !confirmLoading}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ display: 'flex', height: 540, minHeight: 420 }}>
        <div
          style={{
            width: '55%',
            minWidth: 0,
            padding: 20,
            borderRight: '1px solid var(--ant-color-border-secondary)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Input
            allowClear
            value={keyword}
            placeholder={`搜索${selectionTargetLabel}`}
            onChange={(event) => setKeyword(event.target.value)}
            onPressEnter={() => setSearchKeyword(keyword.trim())}
            suffix={
              <Button
                type="text"
                size="small"
                icon={<SearchOutlined />}
                aria-label={`搜索${selectionTargetLabel}`}
                onClick={() => setSearchKeyword(keyword.trim())}
              />
            }
            style={{ marginBottom: 14 }}
          />
          <Space style={{ marginBottom: 10 }}>
            <TeamOutlined />
            <Typography.Text strong>
              {flatEmployees !== undefined ? '全部成员' : '组织架构'}
            </Typography.Text>
          </Space>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {searchKeyword ? (
              searchLoading ? (
                <div style={{ paddingTop: 80, textAlign: 'center' }}>
                  <Spin />
                </div>
              ) : searchResults.length ? (
                searchResults.map((item) => (
                  <div key={item.key} style={{ padding: '8px 4px' }}>
                    <Checkbox
                      checked={selectedKeyMap.has(item.key)}
                      disabled={item.disabled || !canSelect(item.type)}
                      onChange={() => toggleItem(item)}
                    >
                      <Space size={6}>
                        {item.type === 'department' ? (
                          <ApartmentOutlined />
                        ) : (
                          <UserOutlined />
                        )}
                        <span
                          title={
                            disableReadOnlyDepartments && item.readOnly
                              ? item.readOnlyReason
                              : undefined
                          }
                        >
                          {item.path}
                        </span>
                      </Space>
                    </Checkbox>
                  </div>
                ))
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`未找到匹配的${selectionTargetLabel}`}
                />
              )
            ) : initialLoading ? (
              <div style={{ paddingTop: 80, textAlign: 'center' }}>
                <Spin />
              </div>
            ) : tree.length ? (
              <Tree
                showIcon
                checkable
                checkStrictly
                blockNode
                style={{ minWidth: 'max-content' }}
                treeData={tree}
                checkedKeys={checkedTreeKeys}
                loadData={loadNodeContent}
                switcherLoadingIcon={<LoadingOutlined spin />}
                onCheck={(_, info) => {
                  toggleItem((info.node as SelectorTreeNode).selectionItem);
                }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  flatEmployees !== undefined ? '暂无可选成员' : '暂无可见部门'
                }
              />
            )}
          </div>
        </div>
        <div
          style={{
            width: '45%',
            minWidth: 0,
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography.Text type="secondary">
            已选择的{selectionTargetLabel}（{draftValue.length}）
          </Typography.Text>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              marginTop: 14,
              overflowY: 'auto',
            }}
          >
            {draftValue.length ? (
              draftValue.map((item) => (
                <div
                  key={item.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '9px 8px',
                    borderBottom: '1px solid var(--ant-color-border-secondary)',
                  }}
                >
                  <Space size={8} style={{ minWidth: 0 }}>
                    {item.type === 'department' ? (
                      <ApartmentOutlined />
                    ) : (
                      <UserOutlined />
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div
                        title={
                          disableReadOnlyDepartments && item.readOnly
                            ? item.readOnlyReason
                            : undefined
                        }
                      >
                        {item.name}
                      </div>
                      <Typography.Text
                        type="secondary"
                        ellipsis={{ tooltip: item.path }}
                        style={{ maxWidth: 220, fontSize: 12 }}
                      >
                        {item.path}
                      </Typography.Text>
                    </div>
                  </Space>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    disabled={item.disabled}
                    aria-label={`移除${item.name}`}
                    onClick={() => toggleItem(item)}
                  />
                </div>
              ))
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={`暂未选择${selectionTargetLabel}`}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DepartmentMemberSelector;
